import {
  Button,
  Chip,
  faDigits,
  Field,
  formatDate,
  formatWeekday,
  IconButton,
  Panel,
  Seg,
  Switch,
} from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";
import { askReason, ErrorLine, useAction } from "../common.js";
import {
  type EntryRow,
  formatMilli,
  type ItemRow,
  type ListRow,
  type PackageLine,
  type PackageRow,
  parseMilli,
  type RecipeLine,
  type RecipeRow,
  useComponents,
  useItems,
  useMeals,
  useOrgs,
  useTiers,
  weekStart,
} from "./data.js";

export { PricesPage } from "./prices.js";

type Tab = "calendar" | "items" | "lists" | "packages" | "recipes";

/**
 * F3-T12: the manager's menu (design v2 MenuCalendar.html, requirements §5). Everything here is data the manager
 * edits (DL-003): meals, tiers, foods and companions, companion packages per food × tier, and the daily menu,
 * global or per organization (DL-031). Archive, never delete.
 */
export function MenuPage() {
  const [tab, setTab] = useState<Tab>("calendar");
  return (
    <div className="stack">
      <Seg
        label="بخش منو"
        value={tab}
        onChange={setTab}
        options={[
          { value: "calendar", label: "تقویم منو" },
          { value: "items", label: "غذاها و اقلام همراه" },
          { value: "lists", label: "وعده‌ها و سطح‌ها" },
          { value: "packages", label: "بسته‌ی اقلام همراه" },
          { value: "recipes", label: "اجزای غذا" },
        ]}
      />
      {tab === "calendar" && <Calendar />}
      {tab === "items" && <Items />}
      {tab === "lists" && <Lists />}
      {tab === "packages" && <Packages />}
      {tab === "recipes" && <Recipes />}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------

function Calendar() {
  const meals = useMeals();
  const tiers = useTiers();
  const items = useItems();
  const { orgs } = useOrgs();
  const [start, setStart] = useState(() => weekStart(tehranToday()));
  const [mealId, setMealId] = useState<string | null>(null);
  const [audience, setAudience] = useState<string>("global");
  const meal = mealId ?? meals.data?.find((m) => m.active)?.id ?? null;
  const end = addDays(start, 6);
  const query = audience === "global" ? "&global=1" : `&organizationId=${audience}`;
  const entries = useQuery({
    queryKey: ["menu-calendar", start, audience],
    queryFn: () => api<EntryRow[]>(`/menu/calendar?from=${start}&to=${end}${query}`),
  });
  const refresh = [["menu-calendar"]];
  const add = useAction(
    (a: { date: string; menuItemId: string }) =>
      api("/menu/calendar", {
        body: {
          businessDate: a.date,
          mealId: meal,
          menuItemId: a.menuItemId,
          audience: audience === "global" ? { kind: "global" } : { kind: "organization", id: audience },
          tierIds: [],
          sortOrder: (entries.data ?? []).filter((e) => e.businessDate === a.date && e.mealId === meal)
            .length,
        },
      }),
    refresh,
  );
  const remove = useAction(
    (e: EntryRow) => api(`/menu/calendar/${e.id}/remove`, { body: { version: e.version } }),
    refresh,
  );
  const setTiers = useAction(
    (a: { e: EntryRow; tierIds: string[] }) =>
      api(`/menu/calendar/${a.e.id}`, {
        method: "PUT",
        body: { version: a.e.version, tierIds: a.tierIds, sortOrder: a.e.sortOrder },
      }),
    refresh,
  );
  const copy = useAction(async (replace: boolean) => {
    const r = await api<{ copied: number; skippedDays: number }>("/menu/calendar/copy", {
      body: {
        fromDate: start,
        toDate: addDays(start, 7),
        days: 7,
        audience: audience === "global" ? { kind: "global" } : { kind: "organization", id: audience },
        onExisting: replace ? "replace" : "skip",
      },
    });
    window.alert(
      `${faDigits(r.copied)} مورد کپی شد${r.skippedDays ? `؛ ${faDigits(r.skippedDays)} روز که منو داشت دست نخورد` : ""}.`,
    );
  }, refresh);

  const name = (id: string) => items.data?.find((i) => i.id === id)?.name ?? "—";
  const activeTiers = (tiers.data ?? []).filter((t) => t.active);
  const choosable = (items.data ?? []).filter((i) => i.active);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const error = add.error ?? remove.error ?? setTiers.error ?? copy.error;

  return (
    <Panel
      title={`هفته‌ی ${formatDate(start)} تا ${formatDate(end)}`}
      actions={
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <Button
            variant="ghost"
            icon="right"
            onClick={() => {
              setStart(addDays(start, -7));
            }}
          >
            هفته‌ی قبل
          </Button>
          <Button
            variant="ghost"
            icon="left"
            onClick={() => {
              setStart(addDays(start, 7));
            }}
          >
            هفته‌ی بعد
          </Button>
        </div>
      }
    >
      <div className="stack">
        <div className="row wrap" style={{ gap: "var(--s3)", alignItems: "end" }}>
          {meals.data && meal && (
            <Seg
              label="وعده"
              value={meal}
              onChange={setMealId}
              options={meals.data.filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))}
            />
          )}
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="menu-audience">منوی</label>
            <select
              id="menu-audience"
              className="input"
              value={audience}
              onChange={(e) => {
                setAudience(e.target.value);
              }}
            >
              <option value="global">همه‌ی سازمان‌ها (منوی عمومی)</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {"— ".repeat(o.depth)}
                  {faDigits(o.name)} (منوی اختصاصی)
                </option>
              ))}
            </select>
          </div>
          <span className="grow" />
          <Button
            icon="share"
            disabled={copy.busy}
            onClick={() => {
              copy.run(false);
            }}
          >
            کپی این هفته به هفته‌ی بعد
          </Button>
          <Button
            variant="ghost"
            disabled={copy.busy}
            onClick={() => {
              if (window.confirm("منوی هفته‌ی بعد پاک و با این هفته جایگزین شود؟")) copy.run(true);
            }}
          >
            جایگزینی هفته‌ی بعد
          </Button>
        </div>
        {audience !== "global" && (
          <p className="t-cap muted">
            منوی اختصاصی برای این سازمان و زیرمجموعه‌هایش جای منوی عمومی را می‌گیرد؛ روزی که منوی اختصاصی
            ندارد، منوی عمومی نشان داده می‌شود.
          </p>
        )}
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
          {days.map((date) => {
            const dayEntries = (entries.data ?? []).filter(
              (e) => e.businessDate === date && e.mealId === meal,
            );
            return (
              <div key={date} className="card stack" style={{ gap: "var(--s2)" }}>
                <div>
                  <div className="b">{formatWeekday(date)}</div>
                  <div className="t-cap muted">{formatDate(date)}</div>
                </div>
                {dayEntries.map((e) => {
                  const item = items.data?.find((i) => i.id === e.menuItemId);
                  return (
                    <div
                      key={e.id}
                      className="stack"
                      style={{ gap: 4, borderTop: "1px solid var(--card-line)", paddingTop: 6 }}
                    >
                      <div className="row-between">
                        <span className="m">{name(e.menuItemId)}</span>
                        <IconButton
                          icon="x"
                          label={`حذف ${name(e.menuItemId)} از ${formatWeekday(date)}`}
                          onClick={() => {
                            remove.run(e);
                          }}
                        />
                      </div>
                      {item?.kind === "food" && (
                        <div className="row wrap" style={{ gap: 4 }}>
                          {activeTiers.map((t) => {
                            const on = e.tierIds.length === 0 || e.tierIds.includes(t.id);
                            return (
                              <button
                                key={t.id}
                                type="button"
                                className={`chip${on ? " dark" : " plain"}`}
                                aria-pressed={on}
                                onClick={() => {
                                  const current = e.tierIds.length ? e.tierIds : activeTiers.map((x) => x.id);
                                  const next = on ? current.filter((x) => x !== t.id) : [...current, t.id];
                                  if (next.length === 0) return;
                                  setTiers.run({
                                    e,
                                    tierIds: next.length === activeTiers.length ? [] : next,
                                  });
                                }}
                              >
                                {t.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                <select
                  className="input"
                  aria-label={`افزودن به منوی ${formatWeekday(date)}`}
                  value=""
                  disabled={!meal || add.busy}
                  onChange={(ev) => {
                    if (ev.target.value) add.run({ date, menuItemId: ev.target.value });
                  }}
                >
                  <option value="">+ افزودن…</option>
                  {choosable
                    .filter((i) => !dayEntries.some((e) => e.menuItemId === i.id))
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name}
                        {i.kind === "companion" ? " (همراه)" : ""}
                      </option>
                    ))}
                </select>
              </div>
            );
          })}
        </div>
        <ErrorLine error={error} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------------------------

function Items() {
  const items = useItems();
  const [kind, setKind] = useState<"food" | "companion">("food");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const refresh = [["menu-items"]];
  const create = useAction(async () => {
    await api("/menu/items", { body: { code, name, kind, description: "", active: true } });
    setCode("");
    setName("");
  }, refresh);
  const update = useAction(
    (a: { row: ItemRow; patch: Partial<ItemRow> }) =>
      api(`/menu/items/${a.row.id}`, {
        method: "PUT",
        body: {
          version: a.row.version,
          code: a.row.code,
          name: a.row.name,
          kind: a.row.kind,
          description: a.row.description,
          active: a.row.active,
          prepStation: a.row.prepStation,
          ...a.patch,
        },
      }),
    refresh,
  );
  const archive = useAction(async (row: ItemRow) => {
    const reason = askReason(row.name);
    if (reason) await api(`/menu/items/${row.id}/archive`, { body: { version: row.version, reason } });
  }, refresh);
  const rows = (items.data ?? []).filter((i) => i.kind === kind);

  return (
    <Panel title="غذاها و اقلام همراه">
      <div className="stack">
        <Seg
          label="نوع"
          value={kind}
          onChange={setKind}
          options={[
            { value: "food", label: "غذا" },
            { value: "companion", label: "اقلام همراه" },
          ]}
        />
        <form
          className="row wrap"
          style={{ gap: "var(--s2)", alignItems: "end" }}
          onSubmit={(e) => {
            e.preventDefault();
            create.run(undefined);
          }}
        >
          <Field
            id="item-code"
            label="کد"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            style={{ width: 120 }}
          />
          <Field
            id="item-name"
            label={kind === "food" ? "نام غذا" : "نام قلم همراه"}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Button
            type="submit"
            variant="primary"
            icon="plus"
            disabled={!code.trim() || !name.trim() || create.busy}
          >
            افزودن
          </Button>
        </form>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>کد</th>
                <th>نام</th>
                {kind === "companion" && <th>ایستگاه</th>}
                <th>فعال</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="num">{faDigits(r.code)}</td>
                  <td>
                    <button
                      type="button"
                      className="link-v"
                      onClick={() => {
                        const n = window.prompt("نام تازه:", r.name);
                        if (n?.trim()) update.run({ row: r, patch: { name: n.trim() } });
                      }}
                    >
                      {r.name}
                    </button>
                  </td>
                  {kind === "companion" && (
                    <td>
                      <select
                        className="input"
                        aria-label={`ایستگاه آماده‌سازی ${r.name}`}
                        value={r.prepStation ?? "companions"}
                        onChange={(e) => {
                          update.run({
                            row: r,
                            patch: { prepStation: e.target.value === "bread" ? "bread" : "companions" },
                          });
                        }}
                      >
                        <option value="companions">اقلام همراه</option>
                        <option value="bread">نان</option>
                      </select>
                    </td>
                  )}
                  <td>
                    <Switch
                      label={`فعال بودن ${r.name}`}
                      checked={r.active}
                      onChange={(v) => {
                        update.run({ row: r, patch: { active: v } });
                      }}
                    />
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        archive.run(r);
                      }}
                    >
                      بایگانی
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="panel-foot t-cap">
          نوع هر قلم بعد از ساخت عوض نمی‌شود؛ غذا با سطح (معمولی، مخصوص، …) سفارش داده می‌شود و قلم همراه بدون
          سطح. «ایستگاه» می‌گوید قلم همراه در ایستگاه نان آماده می‌شود یا ایستگاه اقلام؛ تعداد نان هر نفر در
          بسته‌ی اقلام همراه تعیین می‌شود.
        </p>
        <ErrorLine error={create.error ?? update.error ?? archive.error} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------------------------

function Lists() {
  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" }}
    >
      <ListEditor
        list="meals"
        title="وعده‌ها"
        hint="ناهار، شام، و هر وعده‌ای که لازم شد (صبحانه، افطار، سحری)"
      />
      <ListEditor list="tiers" title="سطح‌های پرس" hint="معمولی، مخصوص، به‌صرفه، و هر سطحی که لازم شد" />
      <ListEditor
        list="components"
        title="اجزای غذا"
        hint="برنج، جوجه، کباب، خورش، … هر جزء در یک دیگ پخته می‌شود و می‌تواند در چند غذا باشد"
      />
    </div>
  );
}

function ListEditor({
  list,
  title,
  hint,
}: {
  list: "meals" | "tiers" | "components";
  title: string;
  hint: string;
}) {
  const rows = useQuery({
    queryKey: ["menu-lists", list],
    queryFn: () => api<ListRow[]>(`/menu/lists/${list}`),
  });
  const [name, setName] = useState("");
  const refresh = [["menu-lists", list]];
  const create = useAction(async () => {
    await api(`/menu/lists/${list}`, { body: { name, sortOrder: rows.data?.length ?? 0, active: true } });
    setName("");
  }, refresh);
  const update = useAction(
    (a: { row: ListRow; patch: Partial<ListRow> }) =>
      api(`/menu/lists/${list}/${a.row.id}`, {
        method: "PUT",
        body: {
          version: a.row.version,
          name: a.row.name,
          sortOrder: a.row.sortOrder,
          active: a.row.active,
          ...a.patch,
        },
      }),
    refresh,
  );
  const archive = useAction(async (row: ListRow) => {
    const reason = askReason(row.name);
    if (reason)
      await api(`/menu/lists/${list}/${row.id}/archive`, { body: { version: row.version, reason } });
  }, refresh);
  const sorted = [...(rows.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const move = (i: number, dir: -1 | 1) => {
    const a = sorted[i];
    const b = sorted[i + dir];
    if (!a || !b) return;
    update.run({ row: a, patch: { sortOrder: b.sortOrder } });
    update.run({ row: b, patch: { sortOrder: a.sortOrder } });
  };

  return (
    <Panel title={title}>
      <div className="stack">
        <p className="t-cap muted">{hint}</p>
        {sorted.map((r, i) => (
          <div key={r.id} className="row" style={{ gap: "var(--s2)" }}>
            <button
              type="button"
              className="link-v grow"
              style={{ textAlign: "start" }}
              onClick={() => {
                const n = window.prompt("نام تازه:", r.name);
                if (n?.trim()) update.run({ row: r, patch: { name: n.trim() } });
              }}
            >
              {r.name}
            </button>
            <IconButton
              icon="up"
              label={`بالا بردن ${r.name}`}
              disabled={i === 0}
              onClick={() => {
                move(i, -1);
              }}
            />
            <IconButton
              icon="down"
              label={`پایین آوردن ${r.name}`}
              disabled={i === sorted.length - 1}
              onClick={() => {
                move(i, 1);
              }}
            />
            <Switch
              label={`فعال بودن ${r.name}`}
              checked={r.active}
              onChange={(v) => {
                update.run({ row: r, patch: { active: v } });
              }}
            />
            <Button
              variant="ghost"
              onClick={() => {
                archive.run(r);
              }}
            >
              بایگانی
            </Button>
          </div>
        ))}
        <form
          className="row"
          style={{ gap: "var(--s2)", alignItems: "end" }}
          onSubmit={(e) => {
            e.preventDefault();
            create.run(undefined);
          }}
        >
          <Field
            id={`new-${list}`}
            label="افزودن"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Button type="submit" icon="plus" disabled={!name.trim() || create.busy}>
            افزودن
          </Button>
        </form>
        <ErrorLine error={create.error ?? update.error ?? archive.error} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------------------------

function Packages() {
  const items = useItems();
  const tiers = useTiers();
  const { orgs } = useOrgs();
  const foods = (items.data ?? []).filter((i) => i.kind === "food" && i.active);
  const companions = (items.data ?? []).filter((i) => i.kind === "companion" && i.active);
  const [foodId, setFoodId] = useState<string>("");
  const [tierId, setTierId] = useState<string>("");
  const [orgId, setOrgId] = useState<string>("");
  const food = foodId || foods[0]?.id || "";
  const tier = tierId || tiers.data?.find((t) => t.active)?.id || "";
  const pkg = useQuery({
    queryKey: ["menu-package", food, tier, orgId],
    queryFn: () =>
      api<PackageRow | null>(
        `/menu/packages?foodItemId=${food}&tierId=${tier}${orgId ? `&organizationId=${orgId}` : ""}`,
      ),
    enabled: Boolean(food && tier),
  });
  const [draft, setDraft] = useState<PackageLine[] | null>(null);
  const lines = draft ?? pkg.data?.lines ?? [];
  const save = useAction(async () => {
    await api("/menu/packages", {
      method: "PUT",
      body: {
        foodItemId: food,
        tierId: tier,
        organizationId: orgId || null,
        lines,
        version: pkg.data?.version ?? null,
      },
    });
    setDraft(null);
  }, [["menu-package"]]);
  const name = (id: string) => items.data?.find((i) => i.id === id)?.name ?? "—";
  const change = (next: PackageLine[]) => {
    setDraft(next);
  };

  return (
    <Panel title="بسته‌ی اقلام همراه">
      <div className="stack">
        <p className="t-cap muted">
          برای هر غذا و سطح مشخص کنید چه اقلامی همراهش می‌رود (مثلاً معمولی: نوشابه؛ مخصوص: نوشابه و زیتون).
          «جزو قیمت» یعنی جدا حساب نمی‌شود.
        </p>
        <div className="row wrap" style={{ gap: "var(--s3)" }}>
          <div className="field">
            <label htmlFor="pkg-food">غذا</label>
            <select
              id="pkg-food"
              className="input"
              value={food}
              onChange={(e) => {
                setFoodId(e.target.value);
                setDraft(null);
              }}
            >
              {foods.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="pkg-tier">سطح</label>
            <select
              id="pkg-tier"
              className="input"
              value={tier}
              onChange={(e) => {
                setTierId(e.target.value);
                setDraft(null);
              }}
            >
              {(tiers.data ?? [])
                .filter((t) => t.active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="pkg-org">برای</label>
            <select
              id="pkg-org"
              className="input"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setDraft(null);
              }}
            >
              <option value="">همه (بسته‌ی پیش‌فرض)</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {"— ".repeat(o.depth)}
                  {faDigits(o.name)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {orgId && !pkg.data && (
          <Chip plain>این سازمان بسته‌ی خودش را ندارد؛ بسته‌ی پیش‌فرض برایش اعمال می‌شود.</Chip>
        )}
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>قلم همراه</th>
                <th>به‌ازای هر پرس</th>
                <th>جزو قیمت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.companionItemId}>
                  <td>{name(l.companionItemId)}</td>
                  <td>
                    <input
                      className="input num"
                      style={{ width: 90 }}
                      aria-label={`مقدار ${name(l.companionItemId)} به‌ازای هر پرس`}
                      defaultValue={faDigits(formatMilli(l.perPortionMilli))}
                      onBlur={(e) => {
                        const v = parseMilli(e.target.value);
                        if (v !== null)
                          change(lines.map((x, j) => (j === i ? { ...x, perPortionMilli: v } : x)));
                      }}
                    />
                  </td>
                  <td>
                    <Switch
                      label={`${name(l.companionItemId)} جزو قیمت`}
                      checked={l.included}
                      onChange={(v) => {
                        change(lines.map((x, j) => (j === i ? { ...x, included: v } : x)));
                      }}
                    />
                  </td>
                  <td>
                    <IconButton
                      icon="x"
                      label={`حذف ${name(l.companionItemId)}`}
                      onClick={() => {
                        change(lines.filter((_, j) => j !== i));
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <select
            className="input"
            aria-label="افزودن قلم همراه"
            value=""
            onChange={(e) => {
              if (e.target.value)
                change([
                  ...lines,
                  { companionItemId: e.target.value, perPortionMilli: 1000, included: true },
                ]);
            }}
          >
            <option value="">+ افزودن قلم همراه…</option>
            {companions
              .filter((c) => !lines.some((l) => l.companionItemId === c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          <span className="grow" />
          <Button
            variant="primary"
            icon="check"
            disabled={!draft || save.busy}
            onClick={() => {
              save.run(undefined);
            }}
          >
            ذخیره‌ی بسته
          </Button>
        </div>
        <ErrorLine error={save.error} />
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------------------------------------------

/**
 * F4-T02: what each food is made of (architecture §8.5 الف). The planner adds up each component over all foods, so
 * the rice of two dishes is one pot. A food without components is cooked as one piece under its own name.
 */
function Recipes() {
  const items = useItems();
  const components = useComponents();
  const foods = (items.data ?? []).filter((i) => i.kind === "food" && i.active);
  const live = (components.data ?? []).filter((c) => c.active);
  const [foodId, setFoodId] = useState("");
  const food = foodId || foods[0]?.id || "";
  const recipe = useQuery({
    queryKey: ["menu-recipe", food],
    queryFn: () => api<RecipeRow>(`/menu/items/${food}/recipe`),
    enabled: Boolean(food),
  });
  const [draft, setDraft] = useState<RecipeLine[] | null>(null);
  const lines = draft ?? recipe.data?.lines ?? [];
  const save = useAction(async () => {
    await api(`/menu/items/${food}/recipe`, {
      method: "PUT",
      body: { version: recipe.data?.version ?? null, lines },
    });
    setDraft(null);
  }, [["menu-recipe"]]);
  const name = (id: string) => components.data?.find((c) => c.id === id)?.name ?? "—";

  return (
    <Panel title="اجزای غذا">
      <div className="stack">
        <p className="t-cap muted">
          برای هر غذا بگویید از چه اجزایی ساخته می‌شود و به‌ازای هر پرس چقدر (مثلاً چلوجوجه = برنج ۱ + جوجه
          ۱). برنامه‌ی آشپزخانه هر جزء را جدا جمع می‌زند و می‌توانید هر جزء را به آشپزخانه‌ی جدایی بدهید. اجزا
          را در «وعده‌ها و سطح‌ها» تعریف کنید.
        </p>
        <div className="field" style={{ maxWidth: 320 }}>
          <label htmlFor="recipe-food">غذا</label>
          <select
            id="recipe-food"
            className="input"
            value={food}
            onChange={(e) => {
              setFoodId(e.target.value);
              setDraft(null);
            }}
          >
            {foods.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        {lines.length === 0 && (
          <Chip plain>این غذا جزء ندارد؛ در برنامه‌ی آشپزخانه یک‌جا و با نام خودش پخته می‌شود.</Chip>
        )}
        {lines.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>جزء</th>
                  <th>به‌ازای هر پرس</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.componentId}>
                    <td>{name(l.componentId)}</td>
                    <td>
                      <input
                        className="input num"
                        style={{ width: 90 }}
                        aria-label={`مقدار ${name(l.componentId)} به‌ازای هر پرس`}
                        defaultValue={faDigits(formatMilli(l.perPortionMilli))}
                        onBlur={(e) => {
                          const v = parseMilli(e.target.value);
                          if (v !== null && v > 0)
                            setDraft(lines.map((x, j) => (j === i ? { ...x, perPortionMilli: v } : x)));
                        }}
                      />
                    </td>
                    <td>
                      <IconButton
                        icon="x"
                        label={`حذف ${name(l.componentId)}`}
                        onClick={() => {
                          setDraft(lines.filter((_, j) => j !== i));
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="row wrap" style={{ gap: "var(--s2)" }}>
          <select
            className="input"
            aria-label="افزودن جزء"
            value=""
            onChange={(e) => {
              if (e.target.value)
                setDraft([...lines, { componentId: e.target.value, perPortionMilli: 1000 }]);
            }}
          >
            <option value="">+ افزودن جزء…</option>
            {live
              .filter((c) => !lines.some((l) => l.componentId === c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          <span className="grow" />
          <Button
            variant="primary"
            icon="check"
            disabled={!draft || save.busy}
            onClick={() => {
              save.run(undefined);
            }}
          >
            ذخیره‌ی اجزا
          </Button>
        </div>
        <ErrorLine error={save.error} />
      </div>
    </Panel>
  );
}
